import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { ExpandIcon } from "./expand";
import { dollarFormatter } from "../../../util/dollarFormatter";
import ChevronDownIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material";
import { TooltipCombo } from "./TooltipCombo";

const ToolTipComparePositioner = styled("div")`
  position: absolute;
  top: 4px;
  left: 18px;
`;

const ToolTipModalPositioner = styled("div")`
  position: absolute;
  top: calc(50% + 3.25px);
  transform: translate(0, -50%);
  right: 0;
`;

const RelativeParent = styled("div")`
  position: relative;
  width: 100%;
`;

export const PaperItem = styled("div")`
  width: 100%;
  background-color: transparent;
  color: ${(props) => (props.dark ? "black" : "white")};
  border-bottom: 1px dashed
    ${(props) => (props.dark ? "#555" : "rgba(255, 255, 255, 0.3)")};
  text-align: ${(props) => (props.centered ? "center" : "right")};
  padding: ${(props) => props.padding ?? ""};
  @media screen and (max-width: 768px) {
    font-weight: bold;
  }
`;

export const CompareViewAccordion = ({
  title,
  disabled = false,
  tooltipText,
  children,
  name,
  openArray,
  setOpenArray,
  isMini,
  hasIcon,
  customClassName = "",
}) => {
  const isOpen = openArray.includes(name);
  const classNames = [];
  if (isMini) {
    classNames.push("accordion-items");
    classNames.push("child-accordion-items");
    if (isOpen) classNames.push("open-accordion-items");
  } else {
    classNames.push("parent-accordion-items");
  }
  classNames.push(customClassName);
  const className = classNames.join(" ");
  return (
    <RelativeParent className={className}>
      <Accordion
        expanded={openArray.includes(name)}
        onChange={() => {
          if (isOpen) {
            const newOpenArray = openArray.filter((item) => item !== name);
            setOpenArray(newOpenArray);
          } else {
            const newOpenArray = [...openArray, name];
            setOpenArray(newOpenArray);
          }
        }}
        sx={{
          width: "100%",
          boxShadow: "none",
          backgroundColor: "transparent",
          color: "white",
          textAlign: "right",
          "&.Mui-expanded": {
            margin: "0",
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            isMini && !hasIcon ? undefined : (
              <ChevronDownIcon className={`accordion-icon`} />
            )
          }
          sx={{
            display: "flex",
            height: isMini ? "46px" : "73px",
            minHeight: isMini ? "46px" : "73px",
            width: "100%",
            boxShadow: "none",
            border: "none",
            marginLeft: "0",
            padding: {
              xs: "0",
              sm: "0 20px",
              md: "0 20px",
              lg: "0 20px",
            },
            fontWeight: isMini ? "normal" : "bold",
            fontSize: {
              xs: "0.8em",
              sm: isMini ? "1em" : "1.3em",
              md: isMini ? "1em" : "1.3em",
              lg: isMini ? "1em" : "1.3em",
            },
            lineHeight: "1.2em",
            justifyContent: "flex-end",
            flexGrow: "unset",
            fontFamily: "proxima-nova, sans-serif",
            "&.Mui-disabled": {
              opacity: 1,
            },
            "&.Mui-expanded": {
              minHeight: isMini ? "46px" : "64px",
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content": {
              justifyContent: "flex-end",
              flexGrow: "unset",
            },
          }}
        >
          <div className="accordion-summary-inside-wrapper">
            <div className="accordion-summary-title">{title}</div>
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>{children}</AccordionDetails>
      </Accordion>
      <ToolTipComparePositioner>
        <TooltipCombo tip={tooltipText} />
      </ToolTipComparePositioner>
    </RelativeParent>
  );
};

export const QuoteCardAccordion = ({
  title,
  children,
  name,
  openArray,
  isBottom,
}) => {
  const isOpen = openArray.includes(name);
  return (
    <Accordion
      expanded={isOpen}
      sx={{
        width: "100%",
        boxShadow: "none",
        backgroundColor: "transparent",
        color: "white",
        textAlign: "right",
        marginBottom: isBottom ? "10px" : "0",
        "&.Mui-disabled": {
          backgroundColor: "#fff",
        },
        "&.Mui-expanded": {
          margin: "0",
        },
      }}
    >
      <AccordionSummary
        sx={{
          display: "none",
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <div className="accordion-summary-inside-wrapper">
          <div className="accordion-summary-title">{title}</div>
        </div>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          paddingBottom: 0,
          paddingRight: 0,
          paddingLeft: 0,
          paddingTop: 0,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export const AccordionItems = ({ title, amount }) => {
  return (
    <div className="only-flex">
      <div className="accordion-children-left">{title}</div>
      <div className="accordion-children-right">
        {dollarFormatter(Math.round(amount))}
      </div>
    </div>
  );
};


export const ModalAccordion = ({
  title,
  amount = 0,
  add,
  subtract = false,
  disabled = false,
  tooltipText,
  expanded,
  onChange,
  children,
}) => {
  const addOrSubtract = add || subtract;
  const symbol = add ? "+" : "-";
  return (
    <RelativeParent>
      <Accordion
        expanded={expanded}
        onChange={onChange}
        disabled={disabled}
        sx={{
          width: "100%",
          boxShadow: "none",
          borderBottom: "1px solid #ccc",
          "&.Mui-disabled": {
            backgroundColor: "#fff",
          },
          "&.Mui-expanded": {
            margin: "0",
          },
        }}
      >
        <AccordionSummary
          expandIcon={disabled ? undefined : <ExpandIcon />}
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "row-reverse",
            width: disabled ? "calc(100% - 40px)" : "100%",
            gap: "16px",
            boxShadow: "none",
            border: "none",
            marginLeft: disabled ? "40px" : "0",
            fontFamily: "proxima-nova, sans-serif",
            "&.Mui-disabled": {
              opacity: 1,
            },
          }}
        >
          <div className="accordion-summary-inside-wrapper">
            <div className="accordion-summary-title">{title}</div>
            <div className="bold accordion-dollar-amount">
              {`${addOrSubtract ? `${symbol} ` : ""}${dollarFormatter(
                Math.round(amount)
              )}`}
            </div>
          </div>
          <ToolTipModalPositioner>
            <TooltipCombo tip={tooltipText} isViewDetails />
          </ToolTipModalPositioner>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            paddingBottom: 0,
            paddingRight: 0,
            paddingLeft: 0,
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    </RelativeParent>
  );
};
